import { createContext, useContext } from 'react';
import { cn } from '../../lib/cn';

export interface FieldContextValue {
  id: string;
  hintId?: string;
  errorId?: string;
  invalid: boolean;
  required: boolean;
}

export const FieldContext = createContext<FieldContextValue | null>(null);

interface ControlA11yProps {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling';
  required?: boolean;
}

/**
 * Merge the surrounding <Field>'s id / aria-describedby / aria-invalid into a control's props.
 * Every input-like component calls this, so `<Field label="…"><Input {...register('x')} /></Field>` is fully wired.
 */
export function useFieldControl<P extends ControlA11yProps>(props: P): P {
  const ctx = useContext(FieldContext);
  if (!ctx) return props;
  const describedBy = cn(ctx.errorId, ctx.hintId, props['aria-describedby']) || undefined;
  return {
    ...props,
    id: props.id ?? ctx.id,
    'aria-describedby': describedBy,
    'aria-invalid': props['aria-invalid'] ?? (ctx.invalid ? true : undefined),
    required: props.required ?? (ctx.required || undefined),
  };
}

export const controlClass =
  'block w-full min-h-11 rounded-lg border-2 border-border-strong bg-surface px-3 py-2 text-base text-text ' +
  'placeholder:text-muted disabled:cursor-not-allowed disabled:bg-surface-muted ' +
  'aria-[invalid=true]:border-danger aria-[invalid=true]:border-[3px]';
